import '@maplibre/maplibre-gl-leaflet';
import L, { DivIcon, Icon, LatLngExpression } from 'leaflet';
import './patchLeafletZoomAnimation';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet/dist/leaflet.css';
import 'maplibre-gl/dist/maplibre-gl.css';
import React, { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapContainer, Marker, Polyline, TileLayer, useMap } from 'react-leaflet';
import { BoxAvailability, Location, LocationType } from '../../@posturinn/client-react';
import theme from '../../constants/theme';
import usePostboxes from '../../hooks/usePostboxes';
import usePostOffices from '../../hooks/usePostOffices';
import locationIcon from '../../icons/location.svg';
import postofficeIcon from '../../icons/map_postoffice.svg';
import parcelPointDropletHighIcon from '../../icons/parcel-point-droplet-high.svg';
import parcelPointDropletMediumIcon from '../../icons/parcel-point-droplet-medium.svg';
import parcelPointDropletIcon from '../../icons/parcel-point-droplet.svg';
import postBoxDropletHighIcon from '../../icons/postbox-droplet-high.svg';
import postBoxDropletMediumIcon from '../../icons/postbox-droplet-medium.svg';
import postboxIcon from '../../icons/postbox-droplet.svg';
import postOfficeDropletHighIcon from '../../icons/postoffice-droplet-high.svg';
import postOfficeDropletMediumIcon from '../../icons/postoffice-droplet-medium.svg';
import postOfficeDropletIcon from '../../icons/postoffice-droplet.svg';

export interface TravelPathPoint {
	lat: number | string;
	lng: number | string;
}

export interface MapMarker {
	id?: string | number;
	postcode?: string | number;
	latitude?: number | string | null;
	longitude?: number | string | null;
	locationType?: LocationType | string;
	status?: BoxAvailability;
}

type LocationMarker = Location & {
	postcode?: string | number;
	status?: BoxAvailability;
};

type MarkerClickHandler = (item: MapMarker) => void;
type LocationMarkerClickHandler = (locationId: string, lat: number, lng: number, scroll: boolean) => void;

export interface Props {
	lat?: number;
	lng?: number;
	travelPath?: TravelPathPoint[];
	zoom: number;
	selectedMarker?: MapMarker | null;
	activeTab?: number;
	onMarkerClick: MarkerClickHandler;
}

export interface LocationsMapContainerProps
	extends Omit<Props, 'activeTab' | 'onMarkerClick' | 'selectedMarker' | 'travelPath'> {
	onMarkerClick: LocationMarkerClickHandler;
	locations?: LocationMarker[];
	search?: (location: LocationMarker) => boolean;
}

const DEFAULT_CENTER: LatLngExpression = [64.1256369, -21.8756546];
const DEFAULT_BASEMAP_MODE = 'maplibre';
const DEFAULT_MAPLIBRE_STYLE_URL = 'https://tiles.openfreemap.org/styles/positron';
const DEFAULT_RASTER_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const DEFAULT_RASTER_ATTRIBUTION = '&copy; OpenStreetMap contributors';
const DEFAULT_RASTER_SUBDOMAINS = '';
const DEFAULT_MIN_ZOOM = 1;
const DEFAULT_MAX_ZOOM = 19;
const DEFAULT_PROVIDER_TIMEOUT_MS = 8000;
type MarkerPosition = [number, number];
type BaseMapMode = 'maplibre' | 'raster';

const envString = (value?: string): string | null => {
	const next = value?.trim();
	return next ? next : null;
};

const envNumber = (value: string | undefined, fallback: number): number => {
	const next = Number(value);
	return Number.isFinite(next) ? next : fallback;
};

const baseMapMode = (envString(process.env.REACT_APP_BASEMAP_MODE) || DEFAULT_BASEMAP_MODE).toLowerCase();
const mapLibreStyleUrl = envString(process.env.REACT_APP_BASEMAP_STYLE_URL) || DEFAULT_MAPLIBRE_STYLE_URL;
const rasterTileUrl = envString(process.env.REACT_APP_BASEMAP_TILE_URL) || DEFAULT_RASTER_TILE_URL;
const rasterAttribution = envString(process.env.REACT_APP_BASEMAP_ATTRIBUTION) || DEFAULT_RASTER_ATTRIBUTION;
const rasterSubdomains = envString(process.env.REACT_APP_BASEMAP_SUBDOMAINS) || DEFAULT_RASTER_SUBDOMAINS;
const mapMinZoom = envNumber(process.env.REACT_APP_BASEMAP_MIN_ZOOM, DEFAULT_MIN_ZOOM);
const mapMaxZoom = envNumber(process.env.REACT_APP_BASEMAP_MAX_ZOOM, DEFAULT_MAX_ZOOM);
const providerTimeoutMs = envNumber(process.env.REACT_APP_BASEMAP_PROVIDER_TIMEOUT_MS, DEFAULT_PROVIDER_TIMEOUT_MS);

const toNumber = (value: unknown): number | null => {
	const next = Number(value);
	return Number.isFinite(next) ? next : null;
};

const getMarkerPosition = (item?: Pick<MapMarker, 'latitude' | 'longitude'> | null): MarkerPosition | null => {
	const latitude = toNumber(item?.latitude);
	const longitude = toNumber(item?.longitude);

	if (latitude === null || longitude === null) return null;

	return [latitude, longitude];
};

const createIcon = (url: string, size = 40): Icon =>
	L.icon({
		iconUrl: url,
		iconSize: [size, size],
		iconAnchor: [size / 2, size],
		popupAnchor: [0, -size + 8],
	});

const createClusterIcon = (childCount: number): DivIcon =>
	L.divIcon({
		html: `<div style="align-items:center;background:#d61821;border:2px solid #fff;border-radius:50%;color:#fff;display:flex;font-weight:700;height:44px;justify-content:center;line-height:1;width:44px;">${childCount}</div>`,
		className: '',
		iconSize: [44, 44],
	});

const genericIcon = createIcon(locationIcon, 36);
const postOfficeLeafletIcon = createIcon(postofficeIcon, 36);
const postBoxLeafletIcon = createIcon(postboxIcon, 36);

const statusIcons: Record<string, Icon> = {
	ParcelPoint: createIcon(parcelPointDropletIcon, 50),
	ParcelPointHigh: createIcon(parcelPointDropletHighIcon, 50),
	ParcelPointMedium: createIcon(parcelPointDropletMediumIcon, 50),
	PostBox: createIcon(postboxIcon, 50),
	PostBoxHigh: createIcon(postBoxDropletHighIcon, 50),
	PostBoxMedium: createIcon(postBoxDropletMediumIcon, 50),
	PostOffice: createIcon(postOfficeDropletIcon, 50),
	PostOfficeHigh: createIcon(postOfficeDropletHighIcon, 50),
	PostOfficeMedium: createIcon(postOfficeDropletMediumIcon, 50),
};

const MapViewportController: FC<{ center: LatLngExpression; zoom: number; travelPath?: TravelPathPoint[] }> = ({
	center,
	zoom,
	travelPath,
}) => {
	const map = useMap();
	// The callers rebuild `center` and `travelPath` on every render, so keying the effect on their
	// identity re-ran setView/fitBounds constantly and kept restarting zoom animations. Depend on the
	// coordinates themselves instead, and read the path through a ref.
	const [centerLat, centerLng] = center as [number, number];
	const travelPathKey = (travelPath || []).map(point => `${point.lat},${point.lng}`).join('|');
	const travelPathRef = useRef(travelPath);
	travelPathRef.current = travelPath;

	useEffect(() => {
		// Leaflet needs an explicit size recalculation when the map mounts inside tabs and modals.
		const firstPass = window.setTimeout(() => map.invalidateSize(), 0);
		const secondPass = window.setTimeout(() => map.invalidateSize(), 200);
		const currentTravelPath = travelPathRef.current;

		if (currentTravelPath && currentTravelPath.length > 1) {
			const points = currentTravelPath
				.map(point => [toNumber(point.lat), toNumber(point.lng)] as const)
				.filter((point): point is [number, number] => point[0] !== null && point[1] !== null);

			if (points.length > 1) {
				map.fitBounds(points, { padding: [24, 24] });
				return () => {
					window.clearTimeout(firstPass);
					window.clearTimeout(secondPass);
				};
			}
		}

		map.setView([centerLat, centerLng], zoom);

		return () => {
			window.clearTimeout(firstPass);
			window.clearTimeout(secondPass);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [centerLat, centerLng, map, travelPathKey, zoom]);

	return null;
};

const OpenFreeMapLayer: FC<{ onFailure: () => void }> = ({ onFailure }) => {
	const map = useMap();
	const hasFailedRef = useRef(false);

	useEffect(() => {
		const layer = L.maplibreGL({
			style: mapLibreStyleUrl,
		});
		let glMap: ReturnType<typeof layer.getMaplibreMap> | null = null;
		const fail = () => {
			if (hasFailedRef.current) return;

			hasFailedRef.current = true;
			onFailure();
		};
		const timeoutId = window.setTimeout(fail, providerTimeoutMs);
		const handleLoad = () => {
			window.clearTimeout(timeoutId);
		};
		const handleError = () => {
			window.clearTimeout(timeoutId);
			fail();
		};

		try {
			map.addLayer(layer);
			glMap = layer.getMaplibreMap();

			if (!glMap) {
				handleError();
				return () => {
					window.clearTimeout(timeoutId);
					if (map.hasLayer(layer)) map.removeLayer(layer);
				};
			}

			glMap.on('load', handleLoad);
			glMap.on('error', handleError);
		} catch (_error) {
			handleError();
		}

		return () => {
			window.clearTimeout(timeoutId);
			if (glMap) {
				glMap.off('load', handleLoad);
				glMap.off('error', handleError);
			}
			if (map.hasLayer(layer)) map.removeLayer(layer);
		};
	}, [map, onFailure]);

	return null;
};

const RasterBaseMapLayer: FC<{ onFailure: () => void }> = ({ onFailure }) => {
	const [hasLoadedTile, setHasLoadedTile] = useState(false);
	const errorCountRef = useRef(0);

	useEffect(() => {
		if (hasLoadedTile) return;

		const timeoutId = window.setTimeout(() => {
			if (!hasLoadedTile) onFailure();
		}, providerTimeoutMs);

		return () => {
			window.clearTimeout(timeoutId);
		};
	}, [hasLoadedTile, onFailure]);

	return (
		<TileLayer
			attribution={rasterAttribution}
			url={rasterTileUrl}
			subdomains={rasterSubdomains}
			maxZoom={mapMaxZoom}
			eventHandlers={{
				load: () => {
					setHasLoadedTile(true);
				},
				tileerror: () => {
					if (hasLoadedTile) return;

					errorCountRef.current += 1;

					if (errorCountRef.current >= 3) onFailure();
				},
			}}
		/>
	);
};

const MapUnavailableFallback: FC = () => {
	const { t } = useTranslation('common');

	return (
		<div
			style={{
				alignItems: 'center',
				background: '#f5f5f5',
				color: '#4a4a4a',
				display: 'flex',
				fontSize: '2rem',
				height: '100%',
				justifyContent: 'center',
				minHeight: '12rem',
				padding: '1rem',
				textAlign: 'center',
				width: '100%',
			}}>
			{t('misc.map-unavailable')}
		</div>
	);
};

const BaseMap: FC<{
	center: LatLngExpression;
	zoom: number;
	travelPath?: TravelPathPoint[];
	children: React.ReactNode;
}> = ({ center, zoom, travelPath, children }) => {
	const [providerFailed, setProviderFailed] = useState(false);
	const activeBaseMapMode: BaseMapMode = baseMapMode === 'raster' ? 'raster' : 'maplibre';

	if (providerFailed) return <MapUnavailableFallback />;

	return (
		<MapContainer
			center={center}
			zoom={zoom}
			minZoom={mapMinZoom}
			worldCopyJump
			style={{ height: '100%', width: '100%' }}>
			{activeBaseMapMode === 'raster' ? (
				<RasterBaseMapLayer onFailure={() => setProviderFailed(true)} />
			) : (
				<OpenFreeMapLayer onFailure={() => setProviderFailed(true)} />
			)}
			<MapViewportController center={center} zoom={zoom} travelPath={travelPath} />
			{children}
		</MapContainer>
	);
};

const getStatusIcon = (item: LocationMarker): Icon => {
	const suffix =
		item.status === BoxAvailability.High ? 'High' : item.status === BoxAvailability.Medium ? 'Medium' : '';
	return statusIcons[`${item.locationType}${suffix}`] || statusIcons[item.locationType] || genericIcon;
};

const getBasicMarkerIcon = (item: MapMarker, activeTab?: number): Icon => {
	if (item.locationType === LocationType.PostBox || item.locationType === 'PostBox') return postBoxLeafletIcon;
	if (item.locationType === LocationType.PostOffice || item.locationType === 'PostOffice') return postOfficeLeafletIcon;
	if (activeTab === 0) return postOfficeLeafletIcon;
	if (activeTab === 1) return postBoxLeafletIcon;
	return genericIcon;
};

const ClusteredMarkers: FC<{ items: MapMarker[]; onMarkerClick: MarkerClickHandler }> = ({ items, onMarkerClick }) => {
	const map = useMap();

	useEffect(() => {
		const clusterGroup = L.markerClusterGroup({
			iconCreateFunction: cluster => createClusterIcon(cluster.getChildCount()),
		});

		items.forEach(item => {
			const position = getMarkerPosition(item);
			if (!position) return;

			const icon = item.locationType === 'PostOffice' ? postOfficeLeafletIcon : postBoxLeafletIcon;
			const marker = L.marker(position, { icon });
			marker.on('click', () => onMarkerClick(item));
			clusterGroup.addLayer(marker);
		});

		map.addLayer(clusterGroup);

		return () => {
			map.removeLayer(clusterGroup);
		};
	}, [items, map, onMarkerClick]);

	return null;
};

const LocationMapContainer: FC<Props> = ({ selectedMarker, onMarkerClick, lat, lng, travelPath, zoom, activeTab }) => {
	const { postOffices } = usePostOffices();
	const { postboxes } = usePostboxes();
	let itemsToMark: MapMarker[] = activeTab === 0 ? postOffices || [] : postboxes || [];

	if (activeTab === -1) itemsToMark = [{ latitude: lat, longitude: lng }];

	const validItemsToMark = (itemsToMark || []).filter(item => getMarkerPosition(item));

	const center: LatLngExpression = [
		toNumber(selectedMarker?.latitude) ?? toNumber(lat) ?? (DEFAULT_CENTER[0] as number),
		toNumber(selectedMarker?.longitude) ?? toNumber(lng) ?? (DEFAULT_CENTER[1] as number),
	];

	const polylinePath =
		travelPath
			?.map(point => {
				const pathLat = toNumber(point.lat);
				const pathLng = toNumber(point.lng);
				return pathLat !== null && pathLng !== null ? ([pathLat, pathLng] as LatLngExpression) : null;
			})
			.filter((point): point is LatLngExpression => point !== null) || [];

	return (
		<BaseMap center={center} zoom={zoom} travelPath={travelPath}>
			{validItemsToMark.map(item => (
				<Fragment key={`${item.postcode}${item.id}`}>
					<Marker
						position={getMarkerPosition(item) as MarkerPosition}
						icon={getBasicMarkerIcon(item, activeTab)}
						eventHandlers={{ click: () => onMarkerClick(item) }}
					/>
				</Fragment>
			))}
			{polylinePath.length > 1 && (
				<Polyline
					positions={polylinePath}
					pathOptions={{
						color: theme.colors.secondary,
						opacity: 0.7,
						weight: 4,
					}}
				/>
			)}
		</BaseMap>
	);
};

export const MyShipmentsMapContainer: FC<Props> = ({ lat, lng, zoom, onMarkerClick }) => {
	const { postOffices } = usePostOffices();
	const { postboxes } = usePostboxes();
	const center: LatLngExpression = [
		toNumber(lat) ?? (DEFAULT_CENTER[0] as number),
		toNumber(lng) ?? (DEFAULT_CENTER[1] as number),
	];

	return (
		<div style={{ height: '100vh', width: '100%' }}>
			<BaseMap center={center} zoom={zoom}>
				<ClusteredMarkers
					items={[...(postOffices || []), ...(postboxes || [])]}
					onMarkerClick={onMarkerClick}
				/>
			</BaseMap>
		</div>
	);
};

export const LocationsMapContainer: FC<LocationsMapContainerProps> = ({
	lat,
	lng,
	zoom,
	onMarkerClick,
	locations,
	search,
}) => {
	const locationsToRender = (locations?.filter(location => search?.(location) ?? true) || []).filter(location =>
		getMarkerPosition(location)
	);
	const center: LatLngExpression = [
		toNumber(lat) ?? (DEFAULT_CENTER[0] as number),
		toNumber(lng) ?? (DEFAULT_CENTER[1] as number),
	];

	return (
		<BaseMap center={center} zoom={zoom}>
			{locationsToRender.map(item => (
				<Marker
					key={`${item.postcode}${item.id}`}
					position={getMarkerPosition(item) as MarkerPosition}
					icon={getStatusIcon(item)}
					eventHandlers={{ click: () => onMarkerClick(item.id, item.latitude, item.longitude, true) }}
				/>
			))}
		</BaseMap>
	);
};

export default LocationMapContainer;
